import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import {
  POLICY_GROUPS,
  POLICY_KEYS,
  WEEKDAYS,
  type AmountUnit,
  type BranchesResponse,
  type CircleListResponse,
  type PoliciesResponse,
  type PolicyEntry,
  type PolicyKey,
  type PolicyScope,
  type PolicyStatus,
  type ProgramsResponse,
  type Weekday,
} from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  POLICY_META,
  STRUGGLING_FIELDS,
  type StrugglingValue,
  blankPolicyValue,
  formatPolicyValue,
  policyGroupLabel,
  policyScopeLabel,
  unitLabel,
  weekdayLabel,
} from "../lib/policies";
import { useMe } from "../lib/session";

/** Program policies by layer (docs/14): organization → program → branch → circle. */
export function PoliciesPage() {
  const org = useOrg();
  const { data: me } = useMe();
  const orgWide = me?.activeRole === "org_admin";
  const [scopeType, setScopeType] = useState<PolicyScope>(orgWide ? "organization" : "branch");
  const [scopeId, setScopeId] = useState("");
  const [editing, setEditing] = useState<PolicyEntry | null>(null);

  const programs = useQuery({
    queryKey: ["programs", me?.activeRole],
    queryFn: () => api<ProgramsResponse>("/v1/programs"),
  });
  const branches = useQuery({
    queryKey: ["branches", me?.activeRole],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
  });
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, "all"],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
  });
  const choices: { id: string; name: string }[] =
    scopeType === "program"
      ? (programs.data?.programs ?? []).filter((p) => !p.archivedAt)
      : scopeType === "branch"
        ? (branches.data?.branches ?? []).filter((b) => !b.archivedAt)
        : scopeType === "circle"
          ? (circles.data?.circles ?? []).map((c) => ({ id: c.id, name: `${c.name} · ${c.branchName}` }))
          : [];
  // Pick the first entity when the layer type changes.
  useEffect(() => {
    if (scopeType === "organization") setScopeId("");
    else if (!choices.some((c) => c.id === scopeId)) setScopeId(choices[0]?.id ?? "");
  }, [scopeType, choices.length]);

  const ready = !!org && (scopeType === "organization" || !!scopeId);
  const policies = useQuery({
    queryKey: ["policies", org?.id, scopeType, scopeId, me?.activeRole],
    enabled: ready,
    queryFn: () =>
      api<PoliciesResponse>(
        `/v1/policies?${new URLSearchParams({
          organizationId: org!.id,
          scopeType,
          ...(scopeId ? { scopeId } : {}),
        })}`,
      ),
  });

  const layerTypes: PolicyScope[] = ["organization", "program", "branch", "circle"];

  return (
    <>
      <PageTitle
        title="سياسات البرامج"
        sub="القيمة الفعلية لكل إعداد تأتي من أخص مستوى حدّده: الجمعية ← البرنامج ← المجمع ← الحلقة."
      />
      <section className="card">
        <div className="toolbar">
          <select
            aria-label="المستوى"
            value={scopeType}
            onChange={(e) => setScopeType(e.target.value as PolicyScope)}
          >
            {layerTypes.map((s) => (
              <option key={s} value={s}>
                {policyScopeLabel[s]}
              </option>
            ))}
          </select>
          {scopeType !== "organization" && (
            <>
              <select
                aria-label={policyScopeLabel[scopeType]}
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
              >
                {choices.length === 0 && <option value="">لا يوجد</option>}
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        {policies.data && (
          <p className="muted small section" style={{ paddingTop: 0 }}>
            المستويات المؤثرة هنا: {policies.data.layers.map((l) => l.name).join(" ← ")}
            {!policies.data.canEdit && " · للاطلاع فقط"}
          </p>
        )}
      </section>

      {!ready ? (
        <section className="card">
          <Empty title="اختر ما تريد ضبطه" />
        </section>
      ) : policies.isPending ? (
        <section className="card">
          <Loading />
        </section>
      ) : policies.isError ? (
        <section className="card">
          <Empty title="تعذر التحميل">{errorText(policies.error)}</Empty>
        </section>
      ) : (
        POLICY_GROUPS.map((group) => {
          const entries = policies.data.entries.filter((e) => POLICY_KEYS[e.key].group === group);
          return (
            <section className="card" key={group}>
              <div className="card-h">
                <h2>{policyGroupLabel[group]}</h2>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>الإعداد</th>
                      <th>القيمة الفعلية</th>
                      <th>المصدر</th>
                      <th>الحالة</th>
                      <th aria-label="إجراءات" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <PolicyRow
                        key={e.key}
                        entry={e}
                        canEdit={policies.data.canEdit}
                        onEdit={() => setEditing(e)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })
      )}
      {policies.data && (
        <PolicyForm
          entry={editing}
          target={{ organizationId: org!.id, scopeType, scopeId: scopeId || undefined }}
          canApprove={policies.data.canApprove}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: PolicyStatus | null }) {
  if (status === "approved") return <Badge tone="success">معتمد</Badge>;
  if (status === "provisional") return <Badge tone="warning">إعداد أولي غير معتمد</Badge>;
  return <Badge>غير محدد</Badge>;
}

function PolicyRow({ entry, canEdit, onEdit }: { entry: PolicyEntry; canEdit: boolean; onEdit: () => void }) {
  const meta = POLICY_META[entry.key];
  const eff = entry.effective;
  return (
    <tr style={entry.applicable ? undefined : { opacity: 0.55 }}>
      <td>
        <b>{meta.label}</b>
        <div className="muted small">{meta.effect}</div>
      </td>
      <td>{eff ? formatPolicyValue(entry.key, eff.value) : "—"}</td>
      <td>
        {eff ? (
          <span className="small">
            {entry.own ? "هذا المستوى" : `${policyScopeLabel[eff.source.scopeType]}: ${eff.source.name}`}
            {eff.locked && (
              <>
                {" "}
                <Lock size={13} strokeWidth={1.75} aria-label="مقفل" />
              </>
            )}
          </span>
        ) : (
          <span className="muted small">لا يُطبق حتى يُحدد</span>
        )}
      </td>
      <td>
        <StatusBadge status={eff?.status ?? null} />
      </td>
      <td>
        {!entry.applicable ? (
          <span className="muted small">لبرامج التعاهد فقط</span>
        ) : entry.lockedBy ? (
          <span className="muted small">مقفل من {entry.lockedBy.name}</span>
        ) : (
          canEdit && (
            <button className="btn btn-ghost btn-sm" onClick={onEdit}>
              {entry.own ? "تعديل" : "تخصيص"}
            </button>
          )
        )}
      </td>
    </tr>
  );
}

function PolicyForm({
  entry,
  target,
  canApprove,
  onClose,
}: {
  entry: PolicyEntry | null;
  target: { organizationId: string; scopeType: PolicyScope; scopeId?: string };
  canApprove: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { busy, error, run, setError } = useSubmit();
  const [value, setValue] = useState<unknown>(null);
  const [status, setStatus] = useState<PolicyStatus>("provisional");
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (!entry) return;
    const start = entry.own ?? entry.effective;
    setValue(start?.value ?? blankPolicyValue(entry.key));
    setStatus(entry.own?.status === "approved" && canApprove ? "approved" : "provisional");
    setLocked(entry.own?.locked ?? false);
    setError(null);
  }, [entry]);

  if (!entry) return null;
  const meta = POLICY_META[entry.key];
  const refresh = () => qc.invalidateQueries({ queryKey: ["policies"] });

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await api("/v1/policies", {
        method: "PUT",
        json: { ...target, key: entry!.key, value, status, locked },
      });
      await refresh();
      onClose();
    }, errorText);
  }
  function clear() {
    void run(async () => {
      await send("POST", "/v1/policies/clear", { ...target, key: entry!.key });
      await refresh();
      onClose();
    }, errorText);
  }

  return (
    <Modal title={meta.label} open onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <p className="notice small">
          <b>الأثر:</b> {meta.effect} التعديل لا يغيّر النتائج المسجلة سابقًا.
        </p>
        <ValueInput entryKey={entry.key} value={value} onChange={setValue} />
        <Field id="pol-status" label="حالة القيمة">
          <select id="pol-status" value={status} onChange={(e) => setStatus(e.target.value as PolicyStatus)}>
            <option value="provisional">إعداد أولي غير معتمد</option>
            {canApprove && <option value="approved">معتمدة من الجمعية</option>}
          </select>
        </Field>
        <label className="check">
          <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} />
          قفل هذا الإعداد: المستويات الأخص (مثل الحلقات) لا تستطيع تغييره
        </label>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || value === null}>
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
          {entry.own && (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={clear}>
              إزالة التخصيص والرجوع للمستوى الأعم
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}

function ValueInput({
  entryKey,
  value,
  onChange,
}: {
  entryKey: PolicyKey;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const input = POLICY_META[entryKey].input;
  const num = (v: string) => (v === "" ? null : Number(v));
  switch (input.type) {
    case "select":
      return (
        <Field id="pol-value" label="القيمة">
          <select id="pol-value" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            {Object.entries(input.options).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      );
    case "number":
      return (
        <Field id="pol-value" label={input.suffix ? `القيمة (${input.suffix})` : "القيمة"}>
          <input
            id="pol-value"
            type="number"
            dir="ltr"
            min={0}
            step={input.step}
            value={value === null ? "" : String(value)}
            onChange={(e) => onChange(num(e.target.value))}
          />
        </Field>
      );
    case "days": {
      const days = (value as Weekday[] | null) ?? [];
      return (
        <fieldset className="field">
          <legend>أيام الدراسة</legend>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {WEEKDAYS.map((d) => (
              <label key={d} className="check">
                <input
                  type="checkbox"
                  checked={days.includes(d)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? WEEKDAYS.filter((x) => x === d || days.includes(x))
                        : days.filter((x) => x !== d),
                    )
                  }
                />
                {weekdayLabel[d]}
              </label>
            ))}
          </div>
        </fieldset>
      );
    }
    case "amount": {
      const a = (value as { amount: number | null; unit: AmountUnit } | null) ?? { amount: 1, unit: "page" };
      return (
        <div className="form-row">
          <Field id="pol-amount" label="المقدار">
            <input
              id="pol-amount"
              type="number"
              dir="ltr"
              min={0}
              step={0.25}
              value={a.amount ?? ""}
              onChange={(e) => onChange({ ...a, amount: num(e.target.value) })}
            />
          </Field>
          <Field id="pol-unit" label="الوحدة">
            <select
              id="pol-unit"
              value={a.unit}
              onChange={(e) => onChange({ ...a, unit: e.target.value as AmountUnit })}
            >
              {Object.entries(unitLabel).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      );
    }
    case "streak": {
      const s = (value as { enabled: boolean; days: number | null; increment: number | null } | null) ?? {
        enabled: true,
        days: 5,
        increment: 0.5,
      };
      return (
        <>
          <label className="check">
            <input
              type="checkbox"
              checked={s.enabled}
              onChange={(e) => onChange({ ...s, enabled: e.target.checked })}
            />
            مفعّل
          </label>
          <div className="form-row">
            <Field id="pol-days" label="عدد الأيام الناجحة المتتالية">
              <input
                id="pol-days"
                type="number"
                dir="ltr"
                min={1}
                step={1}
                value={s.days ?? ""}
                onChange={(e) => onChange({ ...s, days: num(e.target.value) })}
              />
            </Field>
            <Field id="pol-inc" label="مقدار الزيادة (بوحدة الحفظ)">
              <input
                id="pol-inc"
                type="number"
                dir="ltr"
                min={0}
                step={0.25}
                value={s.increment ?? ""}
                onChange={(e) => onChange({ ...s, increment: num(e.target.value) })}
              />
            </Field>
          </div>
        </>
      );
    }
    case "struggling": {
      const r = value as StrugglingValue;
      return (
        <>
          <div className="form-row">
            <Field id="pol-period" label="الفترة (بالأيام)">
              <input
                id="pol-period"
                type="number"
                dir="ltr"
                min={1}
                step={1}
                value={r.periodDays}
                onChange={(e) => onChange({ ...r, periodDays: num(e.target.value) })}
              />
            </Field>
            <Field
              id="pol-min"
              label="عدد المؤشرات المطلوب"
              hint="مؤشر واحد لا يكفي إلا إذا نصت سياسة البرنامج"
            >
              <input
                id="pol-min"
                type="number"
                dir="ltr"
                min={1}
                max={4}
                step={1}
                value={r.minIndicators}
                onChange={(e) => onChange({ ...r, minIndicators: num(e.target.value) })}
              />
            </Field>
          </div>
          {STRUGGLING_FIELDS.map((f) => (
            <Field key={f.key} id={`pol-${f.key}`} label={f.label} hint="اتركه فارغًا ليُستبعد">
              <input
                id={`pol-${f.key}`}
                type="number"
                dir="ltr"
                min={f.key === "carried" ? 0.25 : 1}
                step={f.key === "carried" ? 0.25 : 1}
                value={r[f.key] ?? ""}
                onChange={(e) => onChange({ ...r, [f.key]: num(e.target.value) })}
              />
            </Field>
          ))}
        </>
      );
    }
    case "weekly": {
      const s = (value as { enabled: boolean; day: Weekday } | null) ?? { enabled: true, day: "sat" };
      return (
        <>
          <label className="check">
            <input
              type="checkbox"
              checked={s.enabled}
              onChange={(e) => onChange({ ...s, enabled: e.target.checked })}
            />
            مفعّلة
          </label>
          <Field id="pol-day" label="اليوم">
            <select
              id="pol-day"
              value={s.day}
              onChange={(e) => onChange({ ...s, day: e.target.value as Weekday })}
            >
              {WEEKDAYS.map((d) => (
                <option key={d} value={d}>
                  {weekdayLabel[d]}
                </option>
              ))}
            </select>
          </Field>
        </>
      );
    }
  }
}
